import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { API_BASE_URL } from "../constants/api";

const inputClass = "w-full px-4 py-2.5 rounded-xl text-sm placeholder-gray-600 focus:outline-none focus:ring-2 transition-colors disabled:opacity-40";
const inputStyle = { backgroundColor: "#111111", border: "1px solid #2a2a2a", color: "#ffffff" };

const DataUsagePage = () => {
  const [mobileNo, setMobileNo] = useState("");
  const [ccid, setCcid] = useState("");
  const [unitId, setUnitId] = useState("");
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    let payload = null;

    if (unitId) {
      payload = { unitId };
    } else if (mobileNo && ccid) {
      payload = { mobileNo, ccid };
    } else {
      alert("Enter either TCU ID OR Mobile + CCID");
      return;
    }

    setLoadingSubmit(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/sim-usage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("API request failed");

      const result = await response.json();
      const usage = result.data;

      navigate("/result", {
        state: [
          {
            mobileNo: usage.mobileNo || mobileNo,
            simNo: usage.simNo || ccid,
            used: usage.usedDataUsage + " " + usage.dataUsageUnits,
            available: usage.remainingDataUsage + " " + usage.dataUsageUnits,
            allocated: usage.totalDataUsage + " " + usage.dataUsageUnits,
            planName: usage.planName,
          },
        ],
      });
    } catch (error) {
      console.error("API error:", error);
      alert("Failed to fetch data usage");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const csvData = event.target.result;
      const parsedData = csvData
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line && !line.toLowerCase().startsWith("mobileno"))
        .map((line) => {
          const [mobile, simNo] = line.split(",").map((v) => v.replace(/\r/g, "").trim());
          return { mobileNo: mobile, simNo };
        });

      if (parsedData.length === 0) { alert("CSV file has no valid rows"); return; }

      setLoadingCsv(true);
      try {
        const response = await fetch(`${API_BASE_URL}/sim/usage/csv`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(parsedData),
        });
        if (!response.ok) throw new Error("Backend CSV API request failed");
        const resultData = await response.json();
        if (!resultData || resultData.length === 0) { alert("No valid rows processed from CSV."); return; }
        navigate("/result", { state: resultData });
      } catch (err) {
        console.error("CSV upload error:", err);
        alert("Failed to process CSV.");
      } finally {
        setLoadingCsv(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <Layout title="Data Usage">
      <div className="max-w-lg">
        <p className="mb-6" style={{ color: "#888888" }}>Enter a TCU ID, or a Mobile Number and CCID to query data usage.</p>

        <div className="rounded-2xl p-8" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "#cccccc" }}>TCU ID</label>
              <input
                type="text"
                placeholder="Enter TCU ID"
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                disabled={!!(mobileNo || ccid)}
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px" style={{ backgroundColor: "#2a2a2a" }} />
              <span className="text-xs font-medium" style={{ color: "#555555" }}>OR</span>
              <div className="flex-1 h-px" style={{ backgroundColor: "#2a2a2a" }} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "#cccccc" }}>Mobile Number</label>
              <input
                type="text"
                placeholder="e.g. 9876543210"
                value={mobileNo}
                onChange={(e) => setMobileNo(e.target.value)}
                disabled={!!unitId}
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "#cccccc" }}>CCID</label>
              <input
                type="text"
                placeholder="Enter CCID"
                value={ccid}
                onChange={(e) => setCcid(e.target.value)}
                disabled={!!unitId}
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <button
              type="submit"
              disabled={loadingSubmit}
              className="w-full py-2.5 text-sm font-semibold rounded-xl transition-colors"
              style={{ backgroundColor: "#ffc20e", color: "#000000", opacity: loadingSubmit ? 0.7 : 1, cursor: loadingSubmit ? "not-allowed" : "pointer" }} // ✅
              onMouseEnter={(e) => { if (!loadingSubmit) e.currentTarget.style.backgroundColor = "#e6ae00"; }} // ✅
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#ffc20e"; }}
            >
              {loadingSubmit ? "Fetching Data..." : "Submit"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ backgroundColor: "#2a2a2a" }} />
            <span className="text-xs font-medium" style={{ color: "#555555" }}>or upload CSV</span>
            <div className="flex-1 h-px" style={{ backgroundColor: "#2a2a2a" }} />
          </div>

          <label
            className="w-full py-2.5 text-sm font-semibold rounded-xl text-center transition-colors block"
            style={{ border: "1px solid #ffc20e", color: loadingCsv ? "#888888" : "#ffc20e", cursor: loadingCsv ? "not-allowed" : "pointer", pointerEvents: loadingCsv ? "none" : "auto" }} // ✅
            onMouseEnter={(e) => { if (!loadingCsv) e.currentTarget.style.backgroundColor = "#ffc20e22"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
          >
            {loadingCsv ? "Fetching Data..." : "Upload CSV"}
            <input type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} disabled={loadingCsv} />
          </label>
        </div>
      </div>
    </Layout>
  );
};

export default DataUsagePage;